import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { Button, Empty, Icon, Modal, Spinner, cx } from "../../components/ui";
import TemplateRaport from "./TemplateRaport";
import { LembarCetak, cetak } from "./cetak";
import { STATUS_BULAN } from "./DaftarMurid";
import { NAMA_RUBRIK, namaBulan, waktuWIB } from "./logika";

// Pratinjau, terbitkan, riwayat & cetak raport satu murid (rancangan §7.3 no. 4–5).
//
// Yang tampil selalu SATU raport: pratinjau isian sekarang (belum terbit, tak bisa dicetak)
// atau salah satu versi terbit (bisa dicetak). Keduanya disusun SERVER — halaman ini tak
// pernah menyusun raport sendiri — jadi yang dipratinjau = yang terbit = yang tercetak.
// Versi yang dipilih disimpan di URL (…/raport/15), supaya refresh tetap di versi itu.

const KET_BULAN = {
  lengkap: "lengkap",
  kosong: "kosong — di raport tertulis “Belum mengikuti kelas”",
  sebagian: "baru terisi sebagian — lengkapi, atau kosongkan bila murid belum ikut kelas"
};

function DaftarBulan({ bulan }) {
  return (
    <ul className="space-y-1.5">
      {bulan.map((b) => (
        <li key={b.ke} className="flex items-start gap-2 text-sm">
          <span className={cx("mt-1 inline-block h-3 w-3 shrink-0 rounded-full border-2", STATUS_BULAN[b.status].titik)} />
          <span>
            <b>Bulan {b.ke} ({namaBulan(b.iso)})</b>{" "}
            <span className={b.status === "sebagian" ? "text-amber-700 dark:text-amber-300" : "text-slate-500"}>
              {KET_BULAN[b.status]}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function TerbitMurid({ classId, studentId, termId, versi, onPilihVersi, onKembali, setToast }) {
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");
  const [terbitan, setTerbitan] = useState({});      // id → raport terbit
  const [galatVersi, setGalatVersi] = useState("");
  const [tanya, setTanya] = useState(false);
  const [sibuk, setSibuk] = useState(false);
  const nomor = useRef(0);

  const muat = useCallback(() => {
    const ini = ++nomor.current;
    return api.getRaportPratinjau(classId, studentId, termId)
      .then((r) => { if (ini === nomor.current) { setData(r); setGalat(""); } })
      .catch((err) => { if (ini === nomor.current) setGalat(err.message || "Pratinjau raport gagal dimuat."); });
  }, [classId, studentId, termId]);

  useEffect(() => { muat(); }, [muat]);

  // Versi terbit yang dipilih: isinya dimuat sekali, lalu disimpan.
  useEffect(() => {
    if (!versi || terbitan[versi]) return undefined;
    let batal = false;
    setGalatVersi("");
    api.getRaportTerbit(versi, classId)
      .then((r) => { if (!batal) setTerbitan((t) => ({ ...t, [versi]: r.snapshot })); })
      .catch((err) => { if (!batal) setGalatVersi(err.message || "Raport terbit gagal dimuat."); });
    return () => { batal = true; };
  }, [versi, classId, terbitan]);

  const terbitkan = async () => {
    setSibuk(true);
    try {
      const r = await api.terbitkanRaport({ term: termId, classId, studentId });
      setTerbitan((t) => ({ ...t, [r.snapshot.id]: r.snapshot }));
      if (setToast) setToast({ type: "success", message: r.message || "Raport diterbitkan." });
      setTanya(false);
      await muat();
      onPilihVersi(r.snapshot.id);
    } catch (err) {
      if (setToast) setToast({ type: "error", message: err.message || "Raport gagal diterbitkan." });
      setTanya(false);
      muat();
    } finally {
      setSibuk(false);
    }
  };

  const kembali = (
    <button
      type="button"
      onClick={onKembali}
      className="mb-3 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-bold text-slate-500 hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
    >
      <Icon name="chevron_left" />
      Kembali ke isian
    </button>
  );

  if (!data) return <div>{kembali}{galat ? <Empty label={galat} /> : <Spinner label="Menyiapkan pratinjau raport…" />}</div>;

  const { raport, syarat, riwayat, term } = data;
  const berlaku = riwayat.find((x) => x.berlaku);
  const terkini = !!berlaku && berlaku.sama;
  const pilihan = versi ? terbitan[versi] : null;
  const tandaPilihan = pilihan && pilihan.dibatalkan ? "DIBATALKAN" : null;

  return (
    <div className="mx-auto max-w-4xl">
      {kembali}
      <div className="anim-rise">
        <h1 className="text-2xl font-black tracking-tight md:text-3xl">Raport {raport.murid.nama}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {raport.murid.studentId} · {raport.kelas.nama} · {NAMA_RUBRIK[raport.rubrik]} · {term.name}
        </p>
      </div>

      <div className={cx("mt-5 grid gap-4", riwayat.length > 0 && "md:grid-cols-2")}>
        <section aria-label="Terbitkan raport" className="anim-rise anim-d1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h2 className="mb-3 text-sm font-black uppercase tracking-wide text-slate-500">Terbitkan raport</h2>
          <DaftarBulan bulan={raport.bulan} />
          {berlaku && (terkini ? (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-300">
              <Icon name="verified" className="text-base" />
              Sudah terbit {waktuWIB(berlaku.pada)} oleh {berlaku.oleh}. Isinya sama dengan isian sekarang.
            </p>
          ) : (
            <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
              <Icon name="published_with_changes" className="text-base" />
              Raport yang terbit {waktuWIB(berlaku.pada)} berbeda dengan isian sekarang. Terbitkan ulang supaya raportnya
              ikut berubah — versi lama tetap tersimpan di riwayat.
            </p>
          ))}
          {!terkini && !syarat.boleh && (
            <p role="note" className="mt-3 flex items-start gap-2 rounded-xl bg-slate-100 p-3 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <Icon name="info" className="text-base" />
              {syarat.alasan}
            </p>
          )}
          {/* Raport yang berlaku sudah tampil di bawah → tak perlu tombol untuk membukanya. */}
          {!(terkini && versi === berlaku.id) && (
            <div className="mt-4">
              {terkini ? (
                <Button onClick={() => onPilihVersi(berlaku.id)} className="w-full sm:w-auto">
                  <Icon name="print" />
                  Lihat & cetak raport terbit
                </Button>
              ) : (
                <Button onClick={() => setTanya(true)} disabled={!syarat.boleh || sibuk} className="w-full sm:w-auto">
                  <Icon name="send" />
                  {berlaku ? "Terbitkan ulang" : "Terbitkan raport"}
                </Button>
              )}
            </div>
          )}
        </section>

        {riwayat.length > 0 && (
          <section aria-label="Riwayat terbit" className="anim-rise anim-d2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="mb-3 text-sm font-black uppercase tracking-wide text-slate-500">Riwayat terbit</h2>
            <ul className="space-y-1.5">
              <li>
                <PilihVersi aktif={!versi} onClick={() => onPilihVersi(null)}>
                  <span className="font-bold">Pratinjau isian sekarang</span>
                  <span className="text-xs text-slate-500">belum terbit</span>
                </PilihVersi>
              </li>
              {riwayat.map((x) => (
                <li key={x.id}>
                  <PilihVersi aktif={versi === x.id} onClick={() => onPilihVersi(x.id)}>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-bold tabular-nums">{waktuWIB(x.pada)}</span>
                      <span
                        className={cx(
                          "rounded-full px-2 py-0.5 text-[10px] font-black uppercase",
                          x.dibatalkan
                            ? "bg-red-500/10 text-red-600 dark:text-red-400"
                            : x.berlaku
                            ? "bg-green-500/10 text-green-700 dark:text-green-300"
                            : "bg-slate-100 text-slate-500 dark:bg-slate-800"
                        )}
                      >
                        {x.dibatalkan ? "Dibatalkan" : x.berlaku ? "Berlaku" : "Versi lama"}
                      </span>
                    </span>
                    <span className="text-xs text-slate-500">
                      {x.oleh}{x.kelas ? ` · ${x.kelas}` : ""}
                      {x.dibatalkan ? ` · alasan: ${x.dibatalkan.alasan}` : ""}
                    </span>
                  </PilihVersi>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <section aria-label="Raport" className="anim-rise anim-d2 mt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          {versi ? (
            pilihan ? (
              <>
                <p className={cx("text-sm font-bold", pilihan.dibatalkan ? "text-red-600 dark:text-red-400" : "text-slate-600 dark:text-slate-300")}>
                  {pilihan.dibatalkan
                    ? `Dibatalkan ${waktuWIB(pilihan.dibatalkan.pada)} oleh ${pilihan.dibatalkan.oleh} — ${pilihan.dibatalkan.alasan}`
                    : `Terbit ${waktuWIB(pilihan.pada)} oleh ${pilihan.oleh}`}
                </p>
                <Button onClick={() => cetak(pilihan.isi)}>
                  <Icon name="print" />
                  Cetak / Simpan PDF
                </Button>
              </>
            ) : null
          ) : (
            <p className="flex items-center gap-2 text-sm font-bold text-slate-500">
              <Icon name="visibility" className="text-base" />
              Pratinjau isian sekarang — belum terbit. Raport bisa dicetak setelah diterbitkan.
            </p>
          )}
        </div>
        {versi && !pilihan ? (
          galatVersi ? <Empty label={galatVersi} /> : <Spinner label="Memuat raport terbit…" />
        ) : (
          <TemplateRaport isi={versi ? pilihan.isi : raport} tanda={versi ? tandaPilihan : "PRATINJAU"} />
        )}
        {pilihan && <LembarCetak key={pilihan.id} isi={pilihan.isi} tanda={tandaPilihan} />}
      </section>

      <Modal title="Terbitkan raport?" open={tanya} onClose={() => { if (!sibuk) setTanya(false); }}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Raport <b>{raport.murid.nama}</b> untuk {term.name} akan diterbitkan dengan isi ini:
        </p>
        <div className="mt-3">
          <DaftarBulan bulan={raport.bulan} />
        </div>
        <p className="mt-4 text-sm text-slate-500">
          Raport yang sudah terbit tak bisa diubah. Bila nanti ada yang salah, perbaiki isiannya lalu terbitkan ulang;
          versi lama tetap tersimpan di riwayat.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => setTanya(false)} disabled={sibuk}>Batal</Button>
          <Button onClick={terbitkan} disabled={sibuk}>
            <Icon name={sibuk ? "progress_activity" : "send"} className={sibuk ? "animate-spin" : ""} />
            {sibuk ? "Menerbitkan…" : "Ya, terbitkan"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function PilihVersi({ aktif, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={aktif}
      onClick={onClick}
      className={cx(
        "flex w-full flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-sm transition-colors",
        aktif
          ? "border-primary bg-primary/10 text-slate-900 dark:text-white"
          : "border-slate-200 text-slate-700 hover:border-primary/50 dark:border-slate-700 dark:text-slate-200"
      )}
    >
      {children}
    </button>
  );
}
